import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Zap, Building2, Truck, Wrench, CheckCircle } from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      id: 'residential',
      icon: <Home size={40} />,
      title: 'Residential EV Charging',
      subtitle: 'Smart Home Charging Solutions',
      description: 'We supply and install convenient EV charging systems for houses, apartments, and residential estates.',
      features: [
        'Portable EV chargers (3 kW / 7 kW)',
        'Wall-mounted smart chargers',
        'Plug-and-play charging solutions',
        'Smart load management',
        'Surge protection',
        'Electrical assessments'
      ],
      image: 'https://customer-assets.emergentagent.com/job_ev-charging-sa/artifacts/jrv4yik5_groundless%20worry%20free%20charging.jpeg'
    },
    {
      id: 'portable',
      icon: <Zap size={40} />,
      title: 'Portable EV Chargers',
      subtitle: 'Flexible Charging Anywhere',
      description: 'Portable chargers designed for convenience, mobility, and emergency charging.',
      features: [
        'Compatible with South African plug standards',
        '3-pin and industrial socket support',
        'Adjustable charging speeds',
        'Compact and travel-friendly',
        'Ideal for road trips and workplaces'
      ],
      image: 'https://customer-assets.emergentagent.com/job_ev-charging-sa/artifacts/wv9h0fam_Portable%20EV%20Charger.jpeg'
    },
    {
      id: 'commercial',
      icon: <Building2 size={40} />,
      title: 'Commercial Charging Infrastructure',
      subtitle: 'Scalable Charging for Businesses',
      description: 'We design and install EV charging systems for shopping centres, fuel stations, hospitals, office parks, and more.',
      features: [
        'AC charging stations',
        'DC fast chargers',
        'Billing and user management systems',
        'Load balancing',
        'Site planning and electrical upgrades',
        'Station branding and signage'
      ],
      image: 'https://images.pexels.com/photos/4678065/pexels-photo-4678065.jpeg?w=800&h=600&fit=crop'
    },
    {
      id: 'fleet',
      icon: <Truck size={40} />,
      title: 'Fleet Charging Solutions',
      subtitle: 'Smart Systems for Electric Fleets',
      description: 'Smart charging systems for businesses transitioning to electric fleets.',
      features: [
        'Multi-vehicle charging management',
        'Energy optimization',
        'Fleet monitoring dashboard',
        'Scheduled charging',
        'Cost tracking and reporting'
      ],
      image: 'https://images.pexels.com/photos/37576213/pexels-photo-37576213.jpeg?w=800&h=600&fit=crop'
    },
    {
      id: 'maintenance',
      icon: <Wrench size={40} />,
      title: 'Maintenance & Support',
      subtitle: 'Ongoing Technical Assistance',
      description: 'Ongoing technical support, diagnostics, and charger servicing.',
      features: [
        'Regular maintenance schedules',
        'Emergency repair services',
        'Software updates',
        'Performance diagnostics',
        '24/7 technical support hotline'
      ],
      image: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&h=600&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen">
      <section
        data-testid="services-hero-section"
        className="relative py-32 bg-gradient-to-br from-[#0A3D2E] to-[#07291F] text-white"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter mb-6 font-['Outfit']">
              Our Services
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Comprehensive EV charging solutions from consultation to ongoing support.
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="services-list-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-24">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              id={service.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="w-20 h-20 bg-[#E9F0EC] rounded-2xl flex items-center justify-center mb-6 text-[#0A3D2E]">
                  {service.icon}
                </div>
                <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-3 font-['Outfit']">
                  {service.title}
                </h2>
                <h3 className="text-xl text-[#CDFF00] font-medium mb-6">{service.subtitle}</h3>
                <p className="text-lg text-[#4A5D54] leading-relaxed mb-8">{service.description}</p>
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <CheckCircle size={20} className="text-[#CDFF00] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-base text-[#4A5D54]">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`service-${service.id}-contact-button`}
                  className="inline-block px-8 py-4 bg-[#0A3D2E] text-white rounded-full font-semibold hover:bg-[#07291F] transition-colors focus:ring-4 focus:ring-[#CDFF00] focus:outline-none"
                >
                  Get a Quote
                </Link>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="rounded-2xl shadow-2xl w-full"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section data-testid="services-cta-section" className="py-24 bg-[#F4F7F5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-6 font-['Outfit']">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-[#4A5D54] mb-8">
              Contact us today for a consultation and site assessment.
            </p>
            <Link
              to="/contact"
              data-testid="services-bottom-cta-button"
              className="inline-block px-8 py-4 bg-[#0A3D2E] text-white rounded-full font-semibold hover:bg-[#07291F] transition-colors focus:ring-4 focus:ring-[#CDFF00] focus:outline-none"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
